/**
 * @module components/ui/TextLink
 * Product link component: Chakra `Link` + theme `link` recipe. See {@link TextLink} for usage.
 */

import { forwardRef, type ReactNode } from 'react';
import { Link as ChakraLink, type LinkProps as ChakraLinkProps } from '@chakra-ui/react';
import { Link as RouterLink, type To } from 'react-router-dom';

/**
 * Visual style for the link recipe (`link.recipe.ts` → `variants.variant`).
 *
 * - **`info`** — Info-colored text, solid underline (Figma “Link / External” look).
 * - **`inline`** — Primary body text, dashed underline (Figma “Link / Internal” look).
 *
 * @see {@link TextLinkLinkScope} when you need outbound vs in-app semantics instead of (or in addition to) look.
 */
export type TextLinkVariant = 'info' | 'inline';

/**
 * Whether the link is treated as **outbound** (`external`) or **in-app** (`internal`).
 *
 * - Affects **default `target` / `rel`** only on the native **`href`** branch (`external` → `_blank` + `noopener noreferrer` unless overridden).
 * - When {@link TextLinkProps.variant} is omitted, chooses the default recipe variant: `external` → `info`, `internal` → `inline`.
 * - Does **not** change how React Router resolves routes; use `to` or `asChild` + `NavLink` for SPA navigation.
 */
export type TextLinkLinkScope = 'external' | 'internal';

/** Typography step: maps to `body.s.regular` / `body.m.regular` in the link recipe. */
type TextLinkSize = 'm' | 'l';

function recipeVariantFromScope(linkScope: TextLinkLinkScope): TextLinkVariant {
  return linkScope === 'external' ? 'info' : 'inline';
}

/**
 * Props for {@link TextLink}.
 *
 * Built from Chakra `LinkProps` with `href`, `to`, `asChild`, `variant`, `size`, and `as` replaced or narrowed here.
 * `linkScope` is consumed by `TextLink` only and is **not** forwarded to Chakra.
 *
 * @property {TextLinkVariant} [variant] — Recipe look (`info` | `inline`). If omitted, derived from `linkScope`.
 * @property {TextLinkLinkScope} [linkScope='internal'] — Outbound vs in-app semantics; see {@link TextLinkLinkScope}.
 * @property {TextLinkSize} [size='l'] — Recipe size (`m` | `l`).
 * @property {string} [href] — Native anchor URL. Used when `asChild` and `to` are not used.
 * @property {To} [to] — React Router destination: **client-side navigation to another route** (whole page view), same as `<Link to>`. Not limited to in-page anchors.
 * @property {boolean} [asChild] — If true, render exactly **one** child element and merge link styles onto it (e.g. `NavLink`).
 * @property {ReactNode} [children] — Link label / content.
 *
 * All other valid Chakra `Link` props (e.g. `onClick`, `className`, responsive styles) pass through except `as` (use `asChild` instead).
 */
export type TextLinkProps = Omit<
  ChakraLinkProps,
  'href' | 'to' | 'as' | 'asChild' | 'variant' | 'size'
> & {
  variant?: TextLinkVariant;
  linkScope?: TextLinkLinkScope;
  size?: TextLinkSize;
  href?: string;
  to?: To;
  /** @inheritdoc — Use with a single child, e.g. `<NavLink to={…}>…</NavLink>`. */
  asChild?: boolean;
  children?: ReactNode;
};

/**
 * Chakra’s styled `Link` supports `asChild` at runtime (`styled-system/factory.js`);
 * generated typings may omit it — assertion used when spreading merged props.
 */
type ChakraLinkWithAsChildProps = ChakraLinkProps & { asChild?: boolean };

/** Default `target` for `href` when `linkScope === 'external'`. */
const EXTERNAL_DEFAULT_TARGET = '_blank';

/** Default `rel` for `href` when `linkScope === 'external'`. */
const EXTERNAL_DEFAULT_REL = 'noopener noreferrer';

/**
 * UI-kit text link: theme recipe + React Router integration.
 *
 * @component
 *
 * @description
 * Renders Chakra `Link` with the `link` recipe. Exactly **one** of these navigation shapes should win per usage:
 *
 * **Styling vs semantics:** Use {@link TextLinkProps.variant} for look and {@link TextLinkProps.linkScope}
 *  for outbound defaults on `href` (and for defaulting `variant` when `variant` is omitted).
 *
 * @param props - {@link TextLinkProps}
 * @param ref - Forwarded to the DOM anchor (or composed element when using `asChild` / router `Link`).
 *
 * @example In-app navigation
 * ```tsx
 * import { TextLink } from 'components/ui/TextLink';
 * import { ProtectedRoutes } from 'types/Routes';
 *
 * <TextLink to={ProtectedRoutes.HOME}>Zur Startseite</TextLink>
 * ```
 *
 * @example External site with secure tab defaults
 * ```tsx
 * <TextLink linkScope="external" href="https://www.example.com">
 *   Externe Seite
 * </TextLink>
 * ```
 *
 * @example Override look while keeping external scope (e.g. dashed style, new tab)
 * ```tsx
 * <TextLink linkScope="external" variant="inline" href="https://example.com">
 *   Mehr erfahren
 * </TextLink>
 * ```
 *
 * @example NavLink — active state on the child, recipe on TextLink
 * ```tsx
 * import { NavLink } from 'react-router-dom';
 *
 * <TextLink linkScope="internal" asChild>
 *   <NavLink
 *     to="/section"
 *     className={({ isActive }) => (isActive ? 'is-active' : undefined)}
 *   >
 *     Abschnitt
 *   </NavLink>
 * </TextLink>
 * ```
 *
 * @example SPA “row” / settings-style row (full route, not a text underline)
 * The recipe targets **inline text links**. For a wide tappable row, keep `to` and **reset** link chrome on the root
 * (or keep using `RouterLink` + layout components if you do not want link recipe tokens at all).
 * ```tsx
 * <TextLink
 *   to={route}
 *   linkScope="internal"
 *   display="block"
 *   w="full"
 *   textDecoration="none"
 *   color="inherit"
 * >
 *   <HStack w="full" py="10px" _hover={{ bg: 'background.secondary' }}>
 *     <Text>{title}</Text>
 *   </HStack>
 * </TextLink>
 * ```
 *
 * @remarks Maintenance
 * - Recipe: `frontend/src/styles/theme/recipes/link.recipe.ts` (keys `info` | `inline`).
 * - Theme registration: `frontend/src/styles/theme/theme.ts` → `recipes.link`.
 * - After recipe changes: `npm run gen:theme-typings`.
 *
 * @see {@link TextLinkProps}
 */
export const TextLink = forwardRef<HTMLAnchorElement, TextLinkProps>((props, ref) => {
  const {
    variant: variantProp,
    linkScope = 'internal',
    size = 'l',
    href,
    to,
    asChild,
    target,
    rel,
    children,
    ...rest
  } = props;

  const recipeVariant = variantProp ?? recipeVariantFromScope(linkScope);

  // 1) Composition: styles merge into one element (NavLink, custom anchor, etc.).
  if (asChild) {
    return (
      <ChakraLink
        ref={ref}
        {...({
          ...rest,
          variant: recipeVariant,
          size,
          asChild: true,
          children,
        } as ChakraLinkWithAsChildProps)}
      />
    );
  }

  // 2) SPA navigation: `to` is a react-router prop — cast the spread for TypeScript.
  if (to != null) {
    return (
      <ChakraLink
        ref={ref}
        as={RouterLink}
        {...({
          ...rest,
          to,
          variant: recipeVariant,
          size,
          target,
          rel,
          children,
        } as ChakraLinkProps)}
      />
    );
  }

  // 3) Native anchor: secure defaults only when explicitly scoped as external.
  const resolvedTarget = linkScope === 'external' ? (target ?? EXTERNAL_DEFAULT_TARGET) : target;
  const resolvedRel = linkScope === 'external' ? (rel ?? EXTERNAL_DEFAULT_REL) : rel;

  return (
    <ChakraLink
      ref={ref}
      href={href}
      variant={recipeVariant}
      size={size}
      target={resolvedTarget}
      rel={resolvedRel}
      {...rest}
    >
      {children}
    </ChakraLink>
  );
});

TextLink.displayName = 'TextLink';
